/**
 * RequirementsFetchLoader
 *
 * Beautiful loader that shows progress while fetching college requirements.
 * - Visual progress for deadlines, essays, and application requirements
 * - Confidence scores as they become available
 * - Student can stay or leave - fetch continues in background
 * - Transitions to verification modal when complete
 */

import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import RequirementsFetchOrchestrator from '../../services/RequirementsFetchOrchestrator';

const RequirementsFetchLoader = ({
  collegeId,
  collegeName,
  userId,
  graduationYear,
  onComplete,
  onCancel
}) => {
  const [fetchStatus, setFetchStatus] = useState('initializing');
  const [progress, setProgress] = useState({
    deadlines: { status: 'pending', confidence: null },
    essays: { status: 'pending', confidence: null },
    requirements: { status: 'pending', confidence: null }
  });
  const [overallConfidence, setOverallConfidence] = useState(null);
  const [error, setError] = useState(null);
  const [userChoice, setUserChoice] = useState(null); // Track user's choice: 'stay' or 'leave'

  useEffect(() => {
    startFetch();

    // Set up real-time subscription to track progress
    const subscription = supabase
      .channel(`fetch-progress-${collegeId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'college_journey_colleges',
          filter: `id=eq.${collegeId}`
        },
        (payload) => {
          handleProgressUpdate(payload.new);
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [collegeId]);

  const startFetch = async () => {
    try {
      setFetchStatus('fetching');

      // Start the fetch process
      const result = await RequirementsFetchOrchestrator.startFetch(
        collegeId,
        collegeName,
        userId,
        graduationYear
      );

      if (result.success) {
        setOverallConfidence(result.confidence);
        setFetchStatus('completed');

        // Update final progress
        setProgress({
          deadlines: {
            status: 'completed',
            confidence: result.data.deadlines?.confidence
          },
          essays: {
            status: 'completed',
            confidence: result.data.essays?.confidence
          },
          requirements: {
            status: 'completed',
            confidence: result.data.components?.confidence
          }
        });

        // Notify parent component
        if (onComplete) {
          onComplete(result);
        }
      } else {
        setFetchStatus('failed');
        setError(result.error);
      }

    } catch (err) {
      console.error('[RequirementsFetchLoader] Fetch error:', err);
      setFetchStatus('failed');
      setError(err.message);
    }
  };

  const handleProgressUpdate = (collegeData) => {
    // Update progress based on database changes
    if (collegeData.requirements_fetch_status === 'completed') {
      setFetchStatus('completed');
      setOverallConfidence(collegeData.requirements_fetch_confidence);
    } else if (collegeData.requirements_fetch_status === 'failed') {
      setFetchStatus('failed');
    }
  };

  const getStepIcon = (status) => {
    switch (status) {
      case 'completed':
        return '✓';
      case 'in_progress':
        return '⟳';
      case 'failed':
        return '✗';
      default:
        return '○';
    }
  };

  const getStepColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-green-600';
      case 'in_progress':
        return 'text-blue-600 animate-spin';
      case 'failed':
        return 'text-red-600';
      default:
        return 'text-gray-400';
    }
  };

  const getConfidenceColor = (confidence) => {
    if (!confidence) return 'text-gray-400';
    if (confidence >= 0.8) return 'text-green-600';
    if (confidence >= 0.6) return 'text-yellow-600';
    return 'text-orange-600';
  };

  const handleStayOnPage = () => {
    // User wants to wait - show feedback
    console.log('[RequirementsFetchLoader] User staying on page');
    setUserChoice('stay');
  };

  const handleReturnToHub = () => {
    // User wants to leave - fetch continues in background
    console.log('[RequirementsFetchLoader] User returning to hub');
    setUserChoice('leave');
    if (onCancel) {
      onCancel();
    }
  };

  if (fetchStatus === 'failed') {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="text-red-600 text-5xl mb-4">✗</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Fetch Failed
            </h2>
            <p className="text-gray-600 mb-6">
              We encountered an error while fetching requirements for {collegeName}.
            </p>
            {error && (
              <div className="bg-red-50 border border-red-200 rounded p-3 mb-6 text-sm text-left">
                <p className="font-mono text-red-800">{error}</p>
              </div>
            )}
            <div className="flex gap-3">
              <button
                onClick={() => window.location.reload()}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Try Again
              </button>
              <button
                onClick={handleReturnToHub}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
              >
                Return to Hub
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (fetchStatus === 'completed') {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="text-green-600 text-5xl mb-4">✓</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Requirements Ready!
            </h2>
            <p className="text-gray-600 mb-6">
              We've collected all the requirements for {collegeName}. Please review and verify the information.
            </p>
            {overallConfidence && (
              <div className="bg-blue-50 border border-blue-200 rounded p-4 mb-6">
                <div className="text-sm text-gray-600 mb-1">Overall Confidence</div>
                <div className={`text-3xl font-bold ${getConfidenceColor(overallConfidence)}`}>
                  {Math.round(overallConfidence * 100)}%
                </div>
              </div>
            )}
            <p className="text-sm text-gray-500 mb-6">
              Ready to verify? This data will be used to generate your personalized timeline.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Fetching in progress
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full mx-4">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Collecting Requirements
          </h2>
          <p className="text-gray-600">
            Gathering the latest information for {collegeName}
          </p>
        </div>

        <div className="space-y-6 mb-8">
          {/* Deadlines Progress */}
          <div className="flex items-center gap-4">
            <div className={`text-2xl ${getStepColor(progress.deadlines.status)}`}>
              {getStepIcon(progress.deadlines.status)}
            </div>
            <div className="flex-1">
              <div className="font-medium text-gray-900">Deadlines</div>
              <div className="text-sm text-gray-500">
                ED, EA, RD, and financial aid deadlines
              </div>
            </div>
            {progress.deadlines.confidence && (
              <div className={`text-sm font-medium ${getConfidenceColor(progress.deadlines.confidence)}`}>
                {Math.round(progress.deadlines.confidence * 100)}%
              </div>
            )}
          </div>

          {/* Essays Progress */}
          <div className="flex items-center gap-4">
            <div className={`text-2xl ${getStepColor(progress.essays.status)}`}>
              {getStepIcon(progress.essays.status)}
            </div>
            <div className="flex-1">
              <div className="font-medium text-gray-900">Essay Requirements</div>
              <div className="text-sm text-gray-500">
                Prompts, word limits, and essay types
              </div>
            </div>
            {progress.essays.confidence && (
              <div className={`text-sm font-medium ${getConfidenceColor(progress.essays.confidence)}`}>
                {Math.round(progress.essays.confidence * 100)}%
              </div>
            )}
          </div>

          {/* Requirements Progress */}
          <div className="flex items-center gap-4">
            <div className={`text-2xl ${getStepColor(progress.requirements.status)}`}>
              {getStepIcon(progress.requirements.status)}
            </div>
            <div className="flex-1">
              <div className="font-medium text-gray-900">Application Requirements</div>
              <div className="text-sm text-gray-500">
                Transcripts, test scores, and recommendations
              </div>
            </div>
            {progress.requirements.confidence && (
              <div className={`text-sm font-medium ${getConfidenceColor(progress.requirements.confidence)}`}>
                {Math.round(progress.requirements.confidence * 100)}%
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={handleStayOnPage}
            disabled={userChoice !== null}
            className={`flex-1 px-4 py-2 text-white rounded transition-all ${
              userChoice === 'stay'
                ? 'bg-teal-700 scale-95'
                : 'bg-teal-600 hover:bg-teal-700 active:scale-95'
            } disabled:opacity-50`}
          >
            {userChoice === 'stay' ? '✓ Staying...' : 'Stay on Page'}
          </button>
          <button
            onClick={handleReturnToHub}
            disabled={userChoice !== null}
            className={`flex-1 px-4 py-2 rounded transition-all ${
              userChoice === 'leave'
                ? 'bg-stone-400 text-white scale-95'
                : 'bg-stone-200 text-slate-800 hover:bg-stone-300 active:scale-95'
            } disabled:opacity-50`}
          >
            {userChoice === 'leave' ? 'Leaving...' : 'Return to Hub'}
          </button>
        </div>

        <p className="text-xs text-stone-500 text-center mt-4">
          {userChoice === 'stay'
            ? 'Great! You\'ll see results here when ready'
            : userChoice === 'leave'
            ? 'Fetch continues in background - we\'ll notify you when ready'
            : 'You can leave this page - we\'ll continue gathering data in the background'}
        </p>
      </div>
    </div>
  );
};

export default RequirementsFetchLoader;
